import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
var _excluded = ["children", "sessionInfo", "allowRedirection"];
//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
// Derived from code from the Material UI project under the following license:
//
// The MIT License (MIT)
//
// Copyright (c) 2014 Call-Em-All
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
//
import React, { useEffect, useState } from "react";
import { createMuiTheme, ThemeProvider, withStyles } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorDialog from "./error/ErrorDialog";
import Login from "./login/Login";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import * as SessionInfo from "./SessionInfo";
import { useSessionInfo } from "./SessionInfo";
import SessionMonitor from "./session/SessionMonitor";
import ErrorBoundary from "./error/ErrorBoundary";
import ErrorMessageDialog from "./configuration/dialog/ErrorMessageDialog";
var theme = createMuiTheme({
  palette: {
    primary: {
      light: "#05f0c6",
      main: "#00A994",
      dark: "#13788e",
      contrastText: "#fff"
    }
  },
  typography: {
    h5: {
      fontWeight: 500,
      fontSize: 26,
      letterSpacing: 0.5
    }
  },
  props: {
    MuiTab: {
      disableRipple: true
    }
  },
  mixins: {
    toolbar: {
      minHeight: 48
    }
  }
});
theme = _objectSpread(_objectSpread({}, theme), {}, {
  overrides: {
    // Scrollbar styling
    MuiCssBaseline: {
      "@global": {
        "*::-webkit-scrollbar": {
          width: "1.0em",
          height: "1.0em"
        },
        "*::-webkit-scrollbar-track": {
          "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,.2)"
        }
      }
    },
    MuiDrawer: {
      paper: {
        backgroundColor: "#ffffff"
      }
    },
    MuiButton: {
      label: {
        textTransform: "none"
      },
      contained: {
        boxShadow: "none",
        "&:active": {
          boxShadow: "none"
        }
      }
    },
    MuiTextField: {
      root: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2)
      }
    },
    MuiTab: {
      root: _defineProperty({
        textTransform: "none",
        margin: "0 16px",
        minWidth: 0,
        padding: 0
      }, theme.breakpoints.up("md"), {
        padding: 0,
        minWidth: 0
      })
    },
    MuiIconButton: {
      root: {
        padding: theme.spacing(1)
      }
    },
    MuiDivider: {
      root: {
        backgroundColor: "#404854"
      }
    },
    MuiListItemText: {
      primary: {
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    MuiListItemIcon: {
      root: {
        color: "inherit",
        marginRight: 0,
        "& svg": {
          fontSize: 20
        }
      }
    },
    MuiAvatar: {
      root: {
        width: 32,
        height: 32
      }
    },
    MUIDataTableBodyCell: {
      root: {
        paddingTop: 1,
        paddingBottom: 1
      }
    },
    MuiTableCell: {
      root: {
        padding: 8
      }
    },
    MuiFormControlLabel: {
      root: {
        marginLeft: 0
      }
    }
  }
});
var styles = {
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(4)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#00A994",
    backgroundColor: "#eaeff1"
  },
  root: {
    display: "flex",
    minHeight: "100vh"
  },
  app: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  main: {
    flex: 1,
    padding: theme.spacing(6, 4),
    background: "#eaeff1"
  },
  footer: {
    padding: theme.spacing(2),
    background: "#eaeff1"
  }
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute(_ref) {
  var children = _ref.children,
    sessionInfo = _ref.sessionInfo,
    allowRedirection = _ref.allowRedirection,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Route, Object.assign({}, rest, {
    render: function render(_ref2) {
      var location = _ref2.location;
      return sessionInfo ? children : allowRedirection && /*#__PURE__*/React.createElement(Redirect, {
        to: {
          pathname: "/login",
          state: {
            from: location
          }
        }
      });
    }
  }));
}
function Application(_ref3) {
  var classes = _ref3.classes,
    application = _ref3.application,
    login = _ref3.login,
    appName = _ref3.appName,
    children = _ref3.children;
  var _useSessionInfo = useSessionInfo(),
    _useSessionInfo2 = _slicedToArray(_useSessionInfo, 2),
    sessionInfo = _useSessionInfo2[0],
    setSessionInfo = _useSessionInfo2[1];
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    previousLocation = _useState2[0],
    setPreviousLocation = _useState2[1];
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    initializationStatus = _React$useState2[0],
    setInitializationStatus = _React$useState2[1];
  var location = useLocation();
  var locationHasChanged = location !== previousLocation;

  // Handle URL path changed
  useEffect(function () {
    if (location.pathname === "/login") {
      setInitializationStatus("success");
    } else if (initializationStatus !== "loading") {
      setInitializationStatus("loading");
      SessionInfo.refresh(function () {
        return setInitializationStatus("success");
      });
    }
    setPreviousLocation(location);
  }, [location]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(SessionMonitor, null), /*#__PURE__*/React.createElement(Backdrop, {
    className: classes.backdrop,
    open: initializationStatus === "loading"
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    color: "inherit"
  })), initializationStatus === "error" && /*#__PURE__*/React.createElement(ErrorMessageDialog, {
    description: "Application not available",
    open: true,
    handleClose: function handleClose() {
      return location.reload();
    }
  }), initializationStatus === "success" && /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(CssBaseline, null), /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
    key: "login",
    path: "/login"
  }, login || /*#__PURE__*/React.createElement(Login, {
    appName: appName
  })), application.routes.map(function (_ref4) {
    var path = _ref4.path,
      component = _ref4.component;
    return /*#__PURE__*/React.createElement(PrivateRoute, {
      key: path,
      path: path,
      sessionInfo: sessionInfo,
      allowRedirection: !locationHasChanged
    }, /*#__PURE__*/React.createElement(ErrorBoundary, null, component));
  }), /*#__PURE__*/React.createElement(Route, {
    key: "no-match",
    path: "*"
  }, /*#__PURE__*/React.createElement(Redirect, {
    to: application.defaultRoute
  }))), children));
}
export default withStyles(styles)(Application);